/**
 * Sankey: columns of nodes joined by links, drawn as ribbons whose width is
 * the value. Thruster Explorer's power flow (generators → switchboard →
 * consumers), made generic. Plan of record: docs/plans/sankey.md.
 *
 * A node is one of four kinds. Signal and Calculation are a Number's value,
 * read on the server exactly as a gauge dial is. Sum of inputs and Remainder
 * read nothing: they are arithmetic over the other nodes, done here. So the
 * server sees only the values; columns, links, labels and the arithmetic
 * nodes travel in the link (`sk`) as a gauge's scale and zones do.
 *
 * Two rules the model keeps:
 *
 * 1. **A ribbon is sized from its end with one partner.** One hub may split
 *    (switchboard → consumers: each ribbon is the consumer) or merge
 *    (generators → switchboard: each ribbon is the generator). A link whose
 *    both ends have several partners has no size and is refused in words.
 * 2. **Nothing is drawn as nothing.** Outflow with no ribbon is named, a
 *    negative Remainder is named, a missing signal is named.
 */
import type { GaugeDialReq, GaugeDialResult, NumberAgg, PlotSpecSankeyNode } from '../../api/types.ts';
import { NUMBER_AGGS, rewriteNumberSignal } from './number-config.ts';

export const SANKEY_MAX_NODES = 24;
export const SANKEY_MAX_COLUMNS = 5;

export type SankeyKind = 'signal' | 'formula' | 'sum' | 'rest';

export const SANKEY_KINDS: Array<{ value: SankeyKind; label: string; title: string }> = [
  { value: 'signal', label: 'Signal', title: 'A signal, summarised over the range' },
  { value: 'formula', label: 'Calculation', title: 'Arithmetic over signal summaries, as a Number' },
  { value: 'sum', label: 'Sum of inputs', title: 'The sum of the ribbons into it; reads no signal' },
  { value: 'rest', label: 'Remainder', title: 'Its input minus that input’s other outputs; reads no signal' },
];

export interface SankeyNodeState {
  /** Stable id the links name (`n1`, `n2`, …); never shown. */
  id: string;
  kind: SankeyKind;
  column: string;
  agg: NumberAgg;
  expression: string;
  label: string;
  /** Column on screen, 0 = left. */
  col: number;
  /** Ids of the nodes in the next column this node flows into. */
  to: string[];
}

/* ── Building ───────────────────────────────────────────── */

/** The next free id. */
export function nextNodeId(nodes: SankeyNodeState[]): string {
  let n = nodes.length + 1;
  const used = new Set(nodes.map((x) => x.id));
  while (used.has(`n${n}`)) n++;
  return `n${n}`;
}

export function newSankeyNode(nodes: SankeyNodeState[], col: number, column = '', kind: SankeyKind = 'signal'): SankeyNodeState {
  return { id: nextNodeId(nodes), kind, column, agg: 'avg', expression: '', label: '', col, to: [] };
}

/**
 * What a fresh Sankey starts from: the signals on screen in the first column,
 * all flowing into one Sum of inputs. Someone opening it on their generators
 * sees a picture at once, and edits it from there.
 */
export function seedSankey(signals: string[]): SankeyNodeState[] {
  const nodes: SankeyNodeState[] = [];
  for (const c of (signals.length ? signals : ['']).slice(0, SANKEY_MAX_NODES - 1)) nodes.push(newSankeyNode(nodes, 0, c));
  const total = { ...newSankeyNode(nodes, 1, '', 'sum'), label: 'Total' };
  for (const n of nodes) n.to = [total.id];
  return [...nodes, total];
}

/** Reads a value from the server: Signal or Calculation. */
export const readsValue = (n: SankeyNodeState): boolean => n.kind === 'signal' || n.kind === 'formula';

/** Whether a node has what the server needs. */
export function nodeReady(n: SankeyNodeState): boolean {
  if (n.kind === 'signal') return !!n.column;
  if (n.kind === 'formula') return !!n.expression.trim();
  return true;
}

/** What a node reads, as the server echoes it (`_gauge_key`). Empty for arithmetic nodes. */
export function nodeKey(n: SankeyNodeState): string {
  if (n.kind === 'formula') return `f:${n.expression}`;
  if (n.kind === 'signal') return `${n.agg}:${n.column}`;
  return '';
}

/** One request entry per distinct value read, in node order. */
export function valuesForRequest(nodes: SankeyNodeState[]): GaugeDialReq[] {
  const seen = new Set<string>();
  const out: GaugeDialReq[] = [];
  for (const n of nodes) {
    if (!readsValue(n) || !nodeReady(n)) continue;
    const key = nodeKey(n);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(n.kind === 'formula' ? { mode: 'formula', expression: n.expression } : { mode: 'signal', column: n.column, agg: n.agg });
  }
  return out;
}

/** Every signal the nodes read, formulas included, in order. */
export function sankeySignals(nodes: SankeyNodeState[]): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    if (n.kind === 'signal') { if (n.column) out.push(n.column); continue; }
    if (n.kind !== 'formula') continue;
    const re = /\b(?:latest|avg|sum|min|max|count)\s*\(\s*(?:"((?:\\.|[^"\\])*)"|'([^']*)'|([A-Za-z_]\w*))\s*\)/gi;
    for (const m of n.expression.matchAll(re)) out.push(m[1] ?? m[2] ?? m[3] ?? '');
  }
  return [...new Set(out.filter(Boolean))];
}

/** What a node is called on screen: its label, else what it reads. */
export function nodeTitle(n: SankeyNodeState): string {
  if (n.label.trim()) return n.label.trim();
  if (n.kind === 'signal') return n.column || 'Signal';
  if (n.kind === 'formula') return n.expression.trim() || 'Calculation';
  return n.kind === 'sum' ? 'Sum of inputs' : 'Remainder';
}

/** Number of columns in use, at least two (a Sankey with one column has nothing to join). */
export function columnCount(nodes: ReadonlyArray<{ col: number }>): number {
  return Math.min(SANKEY_MAX_COLUMNS, Math.max(2, ...nodes.map((n) => n.col + 1)));
}

/**
 * Keep the structure sound after an edit: columns in range, links only to an
 * existing node in the next column, no repeats.
 */
export function tidyNodes(nodes: SankeyNodeState[]): SankeyNodeState[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return nodes.map((n) => {
    const col = Math.max(0, Math.min(SANKEY_MAX_COLUMNS - 1, Math.round(n.col)));
    const to = [...new Set(n.to)].filter((id) => byId.get(id)?.col === col + 1);
    return to.length === n.to.length && col === n.col && to.every((id, i) => id === n.to[i]) ? n : { ...n, col, to };
  });
}

/* ── Structure check ────────────────────────────────────── */

/**
 * What is wrong with the diagram, in a sentence, or null. Plot stays off
 * until it is put right; the sentence says how.
 */
export function sankeyProblem(nodes: SankeyNodeState[]): string | null {
  if (nodes.length < 2) return 'A Sankey needs at least two nodes.';
  const links = nodes.flatMap((n) => n.to.map((to) => [n.id, to] as const));
  if (!links.length) return 'Join at least one node to a node in the next column.';
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outs = (id: string): number => byId.get(id)?.to.length ?? 0;
  const ins = (id: string): number => links.filter((l) => l[1] === id).length;
  for (const [a, b] of links) {
    if (outs(a) > 1 && ins(b) > 1) {
      return `${nodeTitle(byId.get(a)!)} → ${nodeTitle(byId.get(b)!)}: ${nodeTitle(byId.get(a)!)} splits and ${nodeTitle(byId.get(b)!)} merges, so that ribbon has no size. Put a Sum of inputs between them.`;
    }
  }
  for (const n of nodes) {
    if (n.kind === 'sum' && !ins(n.id)) return `${nodeTitle(n)} sums its inputs but has none. Join a node in the column before it.`;
    if (n.kind === 'rest') {
      if (ins(n.id) !== 1) return `${nodeTitle(n)} is a Remainder, so it needs exactly one input.`;
      const src = links.find((l) => l[1] === n.id)![0];
      const twins = byId.get(src)!.to.filter((id) => byId.get(id)?.kind === 'rest');
      if (twins.length > 1) return `${nodeTitle(byId.get(src)!)} has two Remainders. Keep one.`;
    }
  }
  if (!nodes.some(readsValue)) return 'Give at least one node a signal or a calculation.';
  return null;
}

/* ── The model the renderer draws ───────────────────────── */

export type SankeyNodeStatus = 'ok' | 'off' | 'missing' | 'empty' | 'negative' | 'unplotted';

export interface SankeyModelNode {
  id: string;
  title: string;
  kind: SankeyKind;
  col: number;
  value: number | null;
  status: SankeyNodeStatus;
  /** Signals this ship lacks, for `missing`. */
  missing: string[];
  /** Series colour index for Signal and Calculation nodes; -1 for arithmetic ones. */
  colorIndex: number;
}

export interface SankeyModelLink {
  from: string;
  to: string;
  value: number;
  /** The end the ribbon is sized from - and takes its colour from. */
  owner: string;
}

export interface SankeyModel {
  nodes: SankeyModelNode[];
  links: SankeyModelLink[];
  /** Sentences under the chart: gaps, negative remainders, missing signals. */
  notes: string[];
  /** Outflow without a ribbon, by node id: drawn as a hatched stub. */
  unaccounted: Map<string, number>;
}

/**
 * Node values and ribbon sizes for one ship. `results` are the server's
 * values by key; null when this node set was never plotted (a node added since
 * the last Plot says so rather than borrowing a neighbour's value).
 */
export function buildSankeyModel(
  nodes: SankeyNodeState[],
  results: GaugeDialResult[] | null,
  format: (v: number) => string = (v) => String(Math.round(v)),
): SankeyModel {
  const byKey = new Map((results ?? []).map((r) => [r.key, r]));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const links = nodes.flatMap((n) => n.to.filter((to) => byId.has(to)).map((to) => ({ from: n.id, to })));
  const insOf = (id: string): string[] => links.filter((l) => l.to === id).map((l) => l.from);
  const outsOf = (id: string): string[] => byId.get(id)?.to.filter((t) => byId.has(t)) ?? [];
  const out = new Map<string, SankeyModelNode>();
  let colorIndex = 0;
  for (const n of nodes) {
    const base: SankeyModelNode = {
      id: n.id, title: nodeTitle(n), kind: n.kind, col: n.col, value: null, status: 'ok', missing: [],
      colorIndex: readsValue(n) ? colorIndex++ : -1,
    };
    if (readsValue(n)) {
      const r = byKey.get(nodeKey(n));
      if (!r) base.status = 'unplotted';
      else if (r.missing?.length) { base.status = 'missing'; base.missing = r.missing; }
      else if (r.value == null) base.status = 'empty';
      else base.value = r.value;
    }
    out.set(n.id, base);
  }
  // Arithmetic nodes, column by column: a Sum needs the column before it, a
  // Remainder needs its siblings in its own column, which are never Remainders.
  const cols = Math.max(0, ...nodes.map((n) => n.col)) + 1;
  for (let c = 0; c < cols; c++) {
    for (const n of nodes.filter((x) => x.col === c && x.kind === 'sum')) {
      const vals = insOf(n.id).map((id) => out.get(id)!.value);
      const m = out.get(n.id)!;
      if (!vals.length || vals.every((v) => v == null)) m.status = 'empty';
      else m.value = vals.reduce<number>((a, v) => a + Math.max(0, v ?? 0), 0);
    }
    for (const n of nodes.filter((x) => x.col === c && x.kind === 'rest')) {
      const m = out.get(n.id)!;
      const src = insOf(n.id)[0];
      const sv = src ? out.get(src)!.value : null;
      const others = src ? outsOf(src).filter((id) => id !== n.id).map((id) => out.get(id)!.value) : [];
      if (sv == null || others.some((v) => v == null)) { m.status = 'empty'; continue; }
      m.value = sv - others.reduce<number>((a, v) => a + (v ?? 0), 0);
    }
  }
  const notes: string[] = [];
  for (const m of out.values()) {
    if (m.value == null) continue;
    if (m.value < 0) {
      m.status = 'negative';
      notes.push(m.kind === 'rest'
        ? `${m.title} is negative: the other outputs exceed their input by ${format(-m.value)}. The signals are offset in time, or one is wrong.`
        : `${m.title} is negative (${format(m.value)}); it is drawn without ribbons.`);
    } else if (m.value === 0) m.status = 'off';
  }
  // A ribbon takes the value of its end with one partner. Both ends single:
  // the smaller of the two, the flow they agree on.
  const ribbon = (l: { from: string; to: string }): SankeyModelLink => {
    const a = out.get(l.from)!;
    const b = out.get(l.to)!;
    const aSingle = outsOf(l.from).length === 1;
    const bSingle = insOf(l.to).length === 1;
    const owner = aSingle && bSingle ? ((a.value ?? 0) <= (b.value ?? 0) ? a : b) : aSingle ? a : b;
    return { ...l, owner: owner.id, value: Math.max(0, owner.value ?? 0) };
  };
  const ribbons = links.map(ribbon);
  const unaccounted = new Map<string, number>();
  for (const m of out.values()) {
    const outs = ribbons.filter((r) => r.from === m.id);
    if (!outs.length || m.value == null || m.value <= 0) continue;
    const gap = m.value - outs.reduce((a, r) => a + r.value, 0);
    if (gap > 0.02 * m.value) {
      unaccounted.set(m.id, gap);
      // Say why: a node it flows into has no value, or nothing takes the rest.
      const unknown = outs.map((r) => out.get(r.to)!).filter((t) => t.value == null);
      const share = `${format(gap)} of ${m.title} (${Math.round((100 * gap) / m.value)} %) has no ribbon`;
      notes.push(unknown.length
        ? `${share}: ${unknown.map((t) => t.title).join(', ')} ${unknown.length === 1 ? 'has' : 'have'} no value.`
        : outs.some((r) => out.get(r.to)!.kind === 'rest')
          ? `${share}.`
          : `${share}. Add a Remainder node to show it.`);
    }
  }
  // More out of a node than it holds, or more into it than it holds: the
  // renderer narrows those ribbons into the box, and this says why.
  for (const m of out.values()) {
    if (m.value == null || m.value <= 0) continue;
    for (const [dir, list] of [['out of', ribbons.filter((r) => r.from === m.id)], ['into', ribbons.filter((r) => r.to === m.id)]] as const) {
      const sum = list.reduce((a, r) => a + r.value, 0);
      if (list.length && sum > 1.02 * m.value) {
        notes.push(`The ribbons ${dir} ${m.title} add up to ${format(sum)}, more than its ${format(m.value)}, so they narrow into it. Check that the links point the right way.`);
      }
    }
  }
  const missing = [...out.values()].filter((m) => m.status === 'missing');
  if (missing.length) notes.push(`Not on this ship: ${[...new Set(missing.flatMap((m) => m.missing))].join(', ')}.`);
  const empty = [...out.values()].filter((m) => m.status === 'empty' && readsValue(byId.get(m.id)!));
  if (empty.length) notes.push(`No data in this range: ${empty.map((m) => m.title).join(', ')}.`);
  if ([...out.values()].some((m) => m.status === 'unplotted')) notes.push('Plot again to read the nodes added since.');
  return { nodes: [...out.values()], links: ribbons, notes, unaccounted };
}

/* ── The link (`sk`) ────────────────────────────────────── */

interface Wire {
  i: string;
  k?: 'f' | 's' | 'r';
  c?: string;
  a?: string;
  e?: string;
  l?: string;
  o?: number;
  t?: string[];
}

function toWire(n: SankeyNodeState): Wire {
  const w: Wire = { i: n.id };
  if (n.kind === 'formula') { w.k = 'f'; w.e = n.expression; }
  else if (n.kind === 'sum') w.k = 's';
  else if (n.kind === 'rest') w.k = 'r';
  else { w.c = n.column; if (n.agg !== 'avg') w.a = n.agg; }
  if (n.label) w.l = n.label;
  if (n.col) w.o = n.col;
  if (n.to.length) w.t = n.to;
  return w;
}

function fromWire(o: unknown): SankeyNodeState | null {
  if (!o || typeof o !== 'object') return null;
  const w = o as Record<string, unknown>;
  if (typeof w.i !== 'string' || !/^[A-Za-z][A-Za-z0-9]{0,15}$/.test(w.i)) return null;
  const n: SankeyNodeState = { id: w.i, kind: 'signal', column: '', agg: 'avg', expression: '', label: '', col: 0, to: [] };
  if (w.k === 'f') { n.kind = 'formula'; n.expression = typeof w.e === 'string' ? w.e.slice(0, 1000) : ''; }
  else if (w.k === 's') n.kind = 'sum';
  else if (w.k === 'r') n.kind = 'rest';
  else {
    n.column = typeof w.c === 'string' ? w.c.slice(0, 250) : '';
    if (NUMBER_AGGS.some((a) => a.value === w.a)) n.agg = w.a as NumberAgg;
  }
  if (typeof w.l === 'string') n.label = w.l.slice(0, 120);
  if (typeof w.o === 'number' && Number.isInteger(w.o)) n.col = w.o;
  if (Array.isArray(w.t)) n.to = w.t.filter((x): x is string => typeof x === 'string').slice(0, SANKEY_MAX_NODES);
  return n;
}

export interface SankeyDisplay {
  unit: string;
  decimals: number;
  motion: boolean;
}

export const SANKEY_DISPLAY_DEFAULT: SankeyDisplay = { unit: '', decimals: 0, motion: true };

export function serializeSankey(
  s: { sankey_nodes: SankeyNodeState[]; sankey_display: SankeyDisplay },
  params: URLSearchParams,
): void {
  if (s.sankey_nodes.length) params.set('sk', JSON.stringify(s.sankey_nodes.map(toWire)));
  const d = s.sankey_display;
  if (d.unit) params.set('sku', d.unit);
  if (d.decimals !== SANKEY_DISPLAY_DEFAULT.decimals) params.set('skd', String(d.decimals));
  if (!d.motion) params.set('skm', '0');
}

/** `sk`: anything malformed is dropped rather than half-applied. */
export function parseSankeyNodes(raw: string | null): SankeyNodeState[] | null {
  if (!raw) return null;
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return null; }
  if (!Array.isArray(data)) return null;
  const nodes = data.slice(0, SANKEY_MAX_NODES).map(fromWire).filter((n): n is SankeyNodeState => !!n);
  const ids = new Set<string>();
  return tidyNodes(nodes.filter((n) => (ids.has(n.id) ? false : (ids.add(n.id), true))));
}

export function parseSankeyDisplay(params: URLSearchParams): SankeyDisplay {
  const dp = Number(params.get('skd'));
  return {
    unit: (params.get('sku') ?? '').slice(0, 40),
    decimals: params.has('skd') && Number.isInteger(dp) ? Math.max(0, Math.min(10, dp)) : SANKEY_DISPLAY_DEFAULT.decimals,
    motion: params.get('skm') !== '0',
  };
}

/** "DG1, DG2 → Total" for catalog cards and tile titles. */
export function describeSankey(params: URLSearchParams): string {
  const nodes = parseSankeyNodes(params.get('sk')) ?? [];
  if (!nodes.length) return 'Sankey';
  const first = nodes.filter((n) => n.col === 0).map(nodeTitle);
  const last = Math.max(...nodes.map((n) => n.col));
  const end = nodes.filter((n) => n.col === last).map(nodeTitle);
  const list = (xs: string[]): string => (xs.length > 3 ? `${xs.slice(0, 3).join(', ')} +${xs.length - 3}` : xs.join(', '));
  return last > 0 ? `${list(first)} → ${list(end)}` : list(first);
}

/**
 * A derived signal was renamed (`replacement`) or removed (null). A removed
 * signal empties its node rather than deleting it, so its place in the
 * diagram and its links survive for the next signal.
 */
export function rewriteSankeySignal(nodes: SankeyNodeState[], name: string, replacement: string | null): SankeyNodeState[] {
  return nodes.map((n) => {
    if (n.kind === 'signal') return n.column === name ? { ...n, column: replacement ?? '' } : n;
    if (n.kind !== 'formula') return n;
    const expression = rewriteNumberSignal(n.expression, name, replacement);
    return expression === n.expression ? n : { ...n, expression };
  });
}

/* ── The Assistant's proposal shape ─────────────────────── */

/** A proposed node as editor state; `tidyNodes` drops links that break the columns. */
export function nodesFromSpec(spec: PlotSpecSankeyNode[]): SankeyNodeState[] {
  return tidyNodes(spec.slice(0, SANKEY_MAX_NODES).map((n) => ({
    id: n.id, kind: n.kind ?? 'signal', column: n.column ?? '', agg: n.agg ?? 'avg',
    expression: n.expression ?? '', label: n.label ?? '', col: n.col ?? 0, to: n.to ?? [],
  })));
}

/** Editor state back to the proposal shape, for a saved chart the Assistant reads. */
export function nodeToSpec(n: SankeyNodeState): PlotSpecSankeyNode {
  return {
    id: n.id, kind: n.kind,
    ...(n.kind === 'signal' ? { column: n.column, agg: n.agg } : n.kind === 'formula' ? { expression: n.expression } : {}),
    label: n.label, col: n.col, to: n.to,
  };
}
